/**
 * UI State Management Service
 * Handles server-side coordination of floating UI elements via WebSocket
 */

import { getApiUrl } from '../utils/apiUtils';

export interface UIState {
  floating_panel_visible: boolean;
  microphone_position: 'middle-right' | 'bottom-right' | 'bottom-center';
  story_pane_position: 'left' | 'right';
  voice_live_state: 'idle' | 'listening' | 'processing' | 'speaking' | 'error';
  microphone_state?: 'idle' | 'listening' | 'processing' | 'speaking' | 'error';
  microphone_floating?: boolean;
  last_updated: string;
}

export interface UIEvent {
  type: string;
  conversation_id: string;
  data: any;
  timestamp: string;
}

export interface UIEventHandler {
  onFloatingPanelControl: (visible: boolean, trigger_source: string) => void;
  onMicrophoneControl: (position: string, state: string, floating: boolean) => void;
  onUIStateUpdate: (state: UIState) => void;
  onTopicDriftDetected?: (driftData: any) => void;
  onTopicDriftCleared?: (data: any) => void;
  onQuizQuestionDisplay?: (quizData: {
    conversation_id: string;
    question_id: number;
    question_text: string;
    options: string[];
    correct_answer: string;
    difficulty: string;
    question_type: string;
    milestone: string;
  }) => void;
}

export class UIStateService {
  private websocket: WebSocket | null = null;
  private conversationId: string;
  private eventHandlers: UIEventHandler | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;

  constructor(conversationId: string) {
    this.conversationId = conversationId;
  }

  // Connect to UI state WebSocket
  async connect(eventHandlers: UIEventHandler): Promise<void> {
    this.eventHandlers = eventHandlers;
    
    // Close existing connection
    if (this.websocket) {
      this.websocket.close();
    }

    // // Use WebSocket URL directly (proxy will handle routing)
    // const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // const wsHost = window.location.hostname;
    // const wsPort = '8000'; // Backend WebSocket port
    // const fullWsUrl = `${wsProtocol}//${wsHost}:${wsPort}/ui-state/ws/${this.conversationId}`;
    
    // Get WebSocket URL using environment variable or derive from API URL
    const backendWsUrl = process.env.REACT_APP_API_WEBSOCKET_URL || 
                         process.env.REACT_APP_API_URL?.replace('http', 'ws').replace('https', 'wss') || 
                         (window.location.protocol === 'https:' ? 'wss:' : 'ws:') + '//' + window.location.host;
    const fullWsUrl = `${backendWsUrl}/ui-state/ws/${this.conversationId}`;
    console.log('Connecting to UI state WebSocket:', fullWsUrl);
    
    this.websocket = new WebSocket(fullWsUrl);
    
    this.websocket.onopen = () => {
      console.log('UI State WebSocket connected');
      this.reconnectAttempts = 0;
    };

    this.websocket.onmessage = (event) => {
      try {
        const uiEvent: UIEvent = JSON.parse(event.data);
        this.handleUIEvent(uiEvent);
      } catch (error) {
        console.error('Error parsing UI event:', error);
      }
    };

    this.websocket.onclose = (event) => {
      console.log('UI State WebSocket disconnected');
      
      // Only attempt reconnection if backend was running (not initial connection failure)
      if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts && event.code !== 1006) {
        this.attemptReconnection();
      } else if (event.code === 1006 && this.reconnectAttempts === 0) {
        console.warn('Backend server not available - UI will work in standalone mode');
      }
    };

    this.websocket.onerror = (error) => {
      console.error('UI State WebSocket error:', error);
      // Don't log as error if it's just initial connection failure (backend not running)
      if (this.reconnectAttempts === 0) {
        console.warn('Cannot connect to backend - continuing in standalone mode');
      }
    };
  }

  private async attemptReconnection(): Promise<void> {
    this.reconnectAttempts++;
    console.log(`UI State WebSocket reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
    
    await new Promise(resolve => setTimeout(resolve, this.reconnectDelay));
    
    try {
      if (this.eventHandlers) {
        await this.connect(this.eventHandlers);
      }
    } catch (error) {
      console.error('UI State WebSocket reconnection failed:', error);
    }
  }

  private handleUIEvent(event: UIEvent): void {
    if (!this.eventHandlers) return;

    console.log('Received UI event:', event);

    switch (event.type) {
      case 'ui_state_update':
        this.eventHandlers.onUIStateUpdate(event.data as UIState);
        break;
        
      case 'floating_panel_control':
        this.eventHandlers.onFloatingPanelControl(
          event.data.visible,
          event.data.trigger_source
        );
        break;
        
      case 'microphone_control':
        this.eventHandlers.onMicrophoneControl(
          event.data.position || 'middle-right',
          event.data.state || 'idle',
          event.data.floating_style !== false
        );
        break;
        
      case 'topic_drift_detected':
        if (this.eventHandlers.onTopicDriftDetected) {
          console.log('Topic drift detected:', event.data);
          this.eventHandlers.onTopicDriftDetected(event.data);
        }
        break;
        
      case 'topic_drift_cleared':
        if (this.eventHandlers.onTopicDriftCleared) {
          console.log('Topic drift cleared:', event.data);
          this.eventHandlers.onTopicDriftCleared(event.data);
        }
        break;
        
      case 'quiz_question_display':
        if (this.eventHandlers.onQuizQuestionDisplay) {
          console.log('Quiz question received:', event.data);
          this.eventHandlers.onQuizQuestionDisplay(event.data);
        }
        break;
    }
  }

  // Send UI state update to server
  async updateUIState(updates: Partial<UIState>): Promise<void> {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'ui_state_update',
        conversation_id: this.conversationId,
        data: updates
      };
      
      this.websocket.send(JSON.stringify(message));
    }
  }

  // Send VoiceLive event for UI coordination
  async sendVoiceLiveEvent(eventType: string, data: any = {}): Promise<void> {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'voicelive_event',
        conversation_id: this.conversationId,
        event_type: eventType,
        data: data
      };
      
      this.websocket.send(JSON.stringify(message));
    }
    
    // Also make HTTP request for reliability
    try {
      await fetch(getApiUrl('/ui-state/voicelive/ui-event'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: this.conversationId,
          event_type: eventType,
          trigger_reason: 'voicelive_client_event',
          data: data
        })
      });
    } catch (error) {
      // Silently fail if backend is not available - don't spam console
      console.debug('VoiceLive UI event not sent (backend unavailable):', eventType);
    }
  }

  // Control floating conversation panel
  async controlFloatingPanel(action: 'show' | 'hide' | 'toggle', triggerSource: string = 'user_action'): Promise<void> {
    try {
      await fetch(getApiUrl('/ui-state/floating-panel/control'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: this.conversationId,
          action: action,
          trigger_source: triggerSource,
          animation_duration: 500
        })
      });
    } catch (error) {
      console.warn('Error controlling floating panel (backend may not be available):', error);
      // For development, we can still trigger UI changes locally
    }
  }

  // Control microphone button
  async controlMicrophoneButton(
    position: string = 'middle-right', 
    state: string = 'idle',
    floatingStyle: boolean = true
  ): Promise<void> {
    try {
      await fetch(getApiUrl('/ui-state/microphone/control'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: this.conversationId,
          position: position,
          state: state,
          floating_style: floatingStyle
        })
      });
    } catch (error) {
      console.warn('Error controlling microphone button (backend may not be available):', error);
    }
  }

  // Get current UI state
  async getCurrentState(): Promise<UIState | null> {
    try {
      const response = await fetch(getApiUrl(`/ui-state/state/${this.conversationId}`));
      const data = await response.json();
      return data.exists ? data.state : null;
    } catch (error) {
      console.warn('Error getting UI state (backend may not be available):', error);
      return null;
    }
  }

  // Disconnect WebSocket
  disconnect(): void {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
  }
}

// Singleton instance manager
class UIStateManager {
  private instances: Map<string, UIStateService> = new Map();

  getInstance(conversationId: string): UIStateService {
    if (!this.instances.has(conversationId)) {
      this.instances.set(conversationId, new UIStateService(conversationId));
    }
    return this.instances.get(conversationId)!;
  }

  cleanup(conversationId: string): void {
    const instance = this.instances.get(conversationId);
    if (instance) {
      instance.disconnect();
      this.instances.delete(conversationId);
    }
  }

  cleanupAll(): void {
    this.instances.forEach((instance, conversationId) => {
      instance.disconnect();
    });
    this.instances.clear();
  }
}

export const uiStateManager = new UIStateManager();

// Ensure this is treated as a module
export {};